import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Select, Tabs } from 'antd';
import dayjs from 'dayjs';
import { AreasReponse, FloorGroup, Floor } from '../../../model/areas_response';
import { FloorButton } from '../../components/floor_button/floor_button';
import styles from './index.module.scss';
import { Sections } from '../../../constant/index';
import { SummaryItem } from '../../components/summary_item/summary_item';
import { useSessionStore } from '../../../context/session';
import { AreaMap, AreaMapProps } from '../../components/map/map';
import { AreaCollection } from '../../../utils/area_collection';
import { MainScheduleView } from './main_schedule_view';
import { useMainStore } from './main_store';
import { WorkerResponse } from '../../../model/workers_response';
import { ApiError } from '../../../api/api';

/**
 * メイン画面
 * @constructor
 */
export const MainView: React.FC = () => {

  const { 
    areasInfo, 
    selectedDivisionId, 
    setSelectedDivisionId, 
    setSelectedFloorGroupId, 
    selectedFloorGroupId,
    selectedFloorId,
    loginInfo,
  } = useSessionStore((selector) => selector);
  const tmpAreasInfo:AreasReponse = areasInfo;
  const areas = new AreaCollection(tmpAreasInfo);

  const { startGetRobot, stopGetRobot, setHoverWorkerId } = useMainStore((selector) => selector);
  const [robotError, setRobotError] = React.useState<ApiError | null>(null);
  const [robots, setRobots] = React.useState<WorkerResponse>({ robots: [], workers: [] });
  const [robotDay, setRobotDay] = React.useState<dayjs.Dayjs>(null);

  const getFloorGroups = useCallback((): FloorGroup[] => {
    return areas.floorGroups(selectedDivisionId).list;
  }, [selectedDivisionId]);

  const getFloors = (floorGroups: FloorGroup[]): Floor[] => {
    return floorGroups.find((floorGp) => floorGp.id === selectedFloorGroupId).floors;
  };

  const onTabClick = (id: number) => {
    setSelectedDivisionId(id);
  };
  const onFloorGroupTabClick = (id: number) => {
    setSelectedFloorGroupId(id);
  };

  const onGetRobot = (resp: WorkerResponse, error: ApiError) => {
    if (error) {
      setRobots(null);
      setRobotError(error);
      return;
    }
    setRobotError(null);
    setRobots(resp);
    setRobotDay(dayjs());
  };

  useEffect(() => {
    startGetRobot(selectedDivisionId, selectedFloorGroupId, onGetRobot);
  }, [selectedDivisionId, selectedFloorGroupId]);

  const areaMapInfo: AreaMapProps = useMemo(() => {
    const area = new AreaCollection(tmpAreasInfo);
    const res: AreaMapProps = {
      isLoading: true,
      mapCode: '',
      floorCode: '',
      center: [0, 0],
      floorId: 0,
      angle: 0,
      zoom: 0,
      maxZoom: 0,
      minZoom: 0,
    };
    if (!area.divisions().contains(selectedDivisionId)) {
      return res;
    }
    if (!area.floors(selectedDivisionId).contains(selectedFloorId)) {
      return res;
    }
    const div = area.divisions().get(selectedDivisionId);
    const floor = area.floors(selectedDivisionId).get(selectedFloorId);
    const zoom = Math.max(div.zoomMin, Math.min(div.zoomMax, 18));

    return {
      isLoading: false,
      mapCode: div.mapCode,
      floorCode: floor.mapCode,
      floorId: floor.id,
      center: [div.lat, div.lng],
      zoom,
      angle: div.angle,
      maxZoom: div.zoomMax,
      minZoom: div.zoomMin,
    };

  }, [areasInfo, selectedDivisionId, selectedFloorId]);

  useEffect(() => {
    const onTouch = () => {
      setHoverWorkerId('');
    };
    window.addEventListener('touchstart', onTouch);
    return () => {
      window.removeEventListener('touchstart', onTouch);
      stopGetRobot();
    };
  }, []);

  const divisionOptions = useMemo(() => {
    return tmpAreasInfo.map((areaInfo) => {
      return {
        value: areaInfo.id,
        label: areaInfo.name,
      };
    });
  }, [tmpAreasInfo]);

  return (
    <div>
      <h1>メイン</h1>
      <div className={styles.tabs}>
        {
          tmpAreasInfo.map((areaInfo) => {
            if (selectedDivisionId === areaInfo.id) {
              return (
                <input key={`tab-${areaInfo.id}`} className={styles.active} data-active={true} data-testid={`tab-${areaInfo.id}`} id='selectedDivisionId' type='button' onClick={() => onTabClick(areaInfo.id)} value={areaInfo.name}/>
              );
            }
            return (
              <input key={`tab-${areaInfo.id}`} type='button' data-active={false} data-testid={`tab-${areaInfo.id}`} onClick={() => onTabClick(areaInfo.id)} value={areaInfo.name}/>
            );
          })}
      </div>
      <div className={styles.sectionMain}>

        <div>
          <h2 className={styles.contentTitle}>街区情報</h2>
          <div>
            <h3>本日の特記作業件数</h3>
            <div className={styles.item3}>
              <div className={styles.item3Title}></div>
              <div className={styles.summary_items}>
                {Sections.map((section) => (
                   <SummaryItem 
                    key={section.key} 
                    sectionCode={section.key} 
                    sectionName={section.value} 
                   />
                ))}
              </div>              
            </div>
              <MainScheduleView />
            
          </div>
        </div>
        <div>
          <h2 className={styles.contentTitle}>
            <div>
              <div>フロア情報</div>
              <div className={styles.date}>最終取得日時：{robotDay === null ? '-' : robotDay.format('YYYY/MM/DD HH:mm:ss')}</div>
            </div>
          </h2>
          <div>
            { robotError && <Alert message='ロボット情報・従業員情報の取得に失敗しました。' type='error' showIcon={true} className={styles.robotError} /> }
            <div className={styles.floortabs}>
              <Tabs
                className={styles.floorGroups}
                onChange={(key: string) => onFloorGroupTabClick(parseInt(key, 10))}
                type='card'
                activeKey={selectedFloorGroupId.toString()}
                items={getFloorGroups().map((floorGroup) => {
                  return {
                    key: '' + floorGroup.id,
                    label: floorGroup.name,
                  };
                })}
              />

              <Select
                  className={styles.divisionSelector}
                  options={divisionOptions}
                  style={{ width: 200 }}
                  value={selectedDivisionId}
                  onChange={(value) => setSelectedDivisionId(value)}
              />
            </div>
            <div className={styles.sectionFloor}>

              <div className={styles.floorBtns}>
                {getFloors(getFloorGroups()).map((floor) => {
                  return (
                    <FloorButton key={`floorbutton-${floor.id}`} floorInfo={floor} workers={robots} />
                  );
                })}
              </div>
              <div className={styles.map}>
                <AreaMap {...areaMapInfo} robots={robots}/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
